import React, { FunctionComponent, useState, useEffect } from 'react';
import { map, equals, find } from 'ramda';
import { Card } from 'react-bootstrap';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1 } from '../../common/models/callback';
import { IAppState } from '../../common/models/store/IAppState';
import { fetchOwnersRoutine } from './routines';
import { IUserInitials } from '../../common/models/user/IUserInitials';
import SearchInput from '../../components/SearchInput';
import LoaderWrapper from '../../components/LoaderWrapper';
import CheckboxList from '../../components/CheckboxList';
import { IBaseFilter } from '../../common/models/filter/IBaseFilter';
import { Node } from '../../common/models/common/Node';
import { User } from '../../common/models/user/User';
import styles from './styles.module.scss';

interface IOwnerListProps {
  users: IUserInitials[] | null;
  loading: boolean;
  total: number;
  selectedId?: string;
  userId: string;
  onSelect: IBindingCallback1<IUserInitials>;
  fetchUsers: IBindingCallback1<IBaseFilter>;
}

const OwnerList: FunctionComponent<IOwnerListProps> = ({
  users,
  total,
  userId,
  loading,
  selectedId,
  onSelect,
  fetchUsers
}) => {
  const defaultFilter: IBaseFilter = {
    from: 0,
    to: 19,
    input: ''
  };

  const [filter, setFilter] = useState(defaultFilter);

  const nodes = users ? map((user: IUserInitials) => ({
    label: userId === user.id
      ? (
        <span>
          <FormattedMessage id="assignmentFilterBody.me" defaultMessage="Me" />
          &nbsp;(
          {`${user?.firstName} ${user?.lastName}`}
          )
        </span>
      )
      : `${user?.firstName} ${user?.lastName}`,
    value: user?.id
  }), users) : [];

  const selectOwner = ([item]: string[]) => {
    const owner = find(({ id }) => id === item, users as IUserInitials[]) as IUserInitials;
    onSelect(owner);
  };

  const updateFilter = (input: string) => {
    const updatedFilter = { ...filter, input };

    if (!equals(updatedFilter, filter)) {
      setFilter(updatedFilter);
    }
  };

  const loadData = (from: number, to: number) => {
    fetchUsers({ ...filter, from, to });
  };

  useEffect(() => {
    fetchUsers(filter);
  }, [filter]);

  const defaultChecked = find((node: Node) => node.value === selectedId)(nodes)?.value;

  return (
    <Card bsPrefix={styles.card}>
      <div className={styles.searchArea}>
        <SearchInput
          intlId="searchUsers"
          defaultPlaceholder="Search users"
          autoFocus
          value={filter.input}
          onChange={updateFilter}
          width="100%"
          variant="secondary"
        />
      </div>
      <div className={styles.treeWrapper}>
        {loading
          ? <LoaderWrapper loading={loading || !users} />
          : (
            <CheckboxList
              loading={loading}
              total={total}
              items={nodes}
              onSelect={selectOwner}
              loadMore={loadData}
              defaultChecked={defaultChecked ? [defaultChecked] : []}
            />
          )}
      </div>
    </Card>
  );
};

const mapStateToProps = ({
  owners: {
    users,
    loading,
    total
  },
  user: { user }
}: IAppState) => ({ users, loading, total, userId: (user as User).id });

const mapDispachToProps = {
  fetchUsers: fetchOwnersRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(OwnerList);
